import React, { useState, useEffect } from 'react';
import { validateSSOToken } from '../services/api';

export default function DealerClientView() {
  const [clientData,   setClientData]   = useState(null);
  const [bfData,       setBfData]       = useState(null);
  const [holdingsData, setHoldingsData] = useState(null);
  const [dayData,      setDayData]      = useState(null);
  const [orders,       setOrders]       = useState([]);
  const [activeTab,    setActiveTab]    = useState('net');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [placing,      setPlacing]      = useState({});
  const [placed,       setPlaced]       = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  const [toast,        setToast]        = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchWithToken = (url, token) =>
    fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('token');
    if (!token) { setError('Invalid SSO link.'); setLoading(false); return; }

    validateSSOToken(token).then(res => {
      const { ucc, clientName, dealerId } = res.data;
      setClientData({ ucc, clientName, dealerId });
      localStorage.setItem('nb_token', token);

      Promise.all([
        fetchWithToken('/api/bf/positions', token),
        fetchWithToken('/api/holdings', token),
        fetchWithToken('/api/orders/my-orders', token),
        fetchWithToken('/api/dropcopy/positions', token),
      ]).then(([bfRes, holdRes, ordRes, dayRes]) => {
        setBfData(bfRes);
        setHoldingsData(holdRes);
        setOrders(ordRes.orders || []);
        setDayData(dayRes);
        setLoading(false);
      }).catch(() => { setError('Failed to load client data.'); setLoading(false); });

    }).catch(() => {
      setError('SSO token is invalid or expired. Please generate a new link.');
      setLoading(false);
    });
  }, []);

  const handleSquareOff = async () => {
    if (!confirmModal) return;
    const { payload, key } = confirmModal;
    setPlacing(p => ({ ...p, [key]: true }));
    setConfirmModal(null);
    try {
      await fetch('/api/orders/squareoff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('nb_token')}` },
        body: JSON.stringify({ ...payload, placedBy: clientData.dealerId, dealerId: clientData.dealerId })
      }).then(r => r.json());
      showToast(`Square-off placed for ${payload.symbol} by Dealer ${clientData.dealerId}`);
      setPlaced(p => ({ ...p, [key]: true }));
    } catch {
      showToast('Square-off failed. Please try again.', 'error');
    } finally {
      setPlacing(p => ({ ...p, [key]: false }));
    }
  };

  const openConfirm = (payload, key) => setConfirmModal({ payload, key });

  const fmt = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const fmtQty = (val) => {
    if (val === null || val === undefined) return '0';
    const n = Number(val);
    return Number.isInteger(n) ? n.toString() : n.toFixed(2);
  };

  const fmtStrike = (val) => val ? Number(val).toLocaleString('en-IN') : null;

  const EX = {
    NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  };

  if (loading) return <div style={s.center}><div style={{ fontSize: 14, color: '#64748b' }}>Authenticating SSO session...</div></div>;
  if (error)   return <div style={s.center}><div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 12, padding: '32px 40px', maxWidth: 400, textAlign: 'center' }}><div style={{ fontSize: 14, color: '#dc2626', fontWeight: 500 }}>{error}</div></div></div>;

  // ── Data ──────────────────────────────────────────────────────────────────
  const bfOptions  = bfData?.options  || [];
  const bfFutures  = bfData?.futures  || [];
  const dayPos     = dayData?.positions || [];
  const dayEquity  = dayPos.filter(p => p.instrument_type === 'EQUITY');
  const dayOptions = dayPos.filter(p => p.instrument_type === 'OPTIONS');
  const dayFutures = dayPos.filter(p => p.instrument_type === 'FUTURES');
  const dayEqOpen  = dayEquity.filter(p => Number(p.net_qty) !== 0);

  // ── Merge B/F + Day F&O into single net rows ──────────────────────────────
  const mergeKey = (symbol, expiry, strike, optType) =>
    `${symbol}|${(expiry||'').slice(0,10)}|${strike||''}|${optType||''}`;

  const netMap = new Map();

  [...bfOptions, ...bfFutures].forEach(pos => {
    const key    = mergeKey(pos.symbol, pos.expiry_date, pos.strike_price, pos.option_type);
    const bfLots = Number(pos.qty_in_lots ?? pos.total_open_qty) || 0;
    if (!netMap.has(key)) {
      netMap.set(key, {
        symbol: pos.symbol, exchange: pos.exchange, segment: pos.segment || 'FO',
        expiry_date: pos.expiry_date, strike_price: pos.strike_price,
        option_type: pos.option_type, instrument_type: pos.instrument_type,
        bf_qty: bfLots, day_qty: 0, net_qty: bfLots, bf_id: pos.bf_id,
      });
    } else {
      const e = netMap.get(key);
      e.bf_qty += bfLots; e.net_qty += bfLots;
    }
  });

  [...dayOptions, ...dayFutures].forEach(pos => {
    const key    = mergeKey(pos.symbol, pos.expiry_date, pos.strike_price, pos.option_type);
    const dayNet = Number(pos.net_qty) || 0;
    if (!netMap.has(key)) {
      netMap.set(key, {
        symbol: pos.symbol, exchange: pos.exchange, segment: pos.segment || 'FO',
        expiry_date: pos.expiry_date, strike_price: pos.strike_price,
        option_type: pos.option_type, instrument_type: pos.instrument_type,
        bf_qty: 0, day_qty: dayNet, net_qty: dayNet, bf_id: null,
      });
    } else {
      const e = netMap.get(key);
      e.day_qty += dayNet; e.net_qty += dayNet;
    }
  });

  const netFOPositions = Array.from(netMap.values());
  const netCount = netFOPositions.length;

  const tabs = [
    { key: 'net',      label: 'Net Position',  count: netCount },
    { key: 'day',      label: 'Day Position',  count: dayPos.length },
    { key: 'bf',       label: 'B/F Position',  count: bfData?.total_positions ?? 0 },
    { key: 'holdings', label: 'Holdings',      count: holdingsData?.total_holdings ?? 0 },
    { key: 'orders',   label: 'My Orders',     count: orders.length },
  ];

  // ── Reusable components ───────────────────────────────────────────────────
  const ExBadge = ({ exchange, segment }) => {
    const ex = EX[exchange] || { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' };
    return (
      <span style={{ ...s.exBadge, background: ex.bg, color: ex.color, border: `0.5px solid ${ex.border}` }}>
        {exchange}<br />{segment}
      </span>
    );
  };

  const TypeChip = ({ type }) => {
    if (!type) return <span style={s.futBadge}>FUT</span>;
    return (
      <span style={{ ...s.chip, background: type === 'CE' ? '#f0fdf4' : '#fef2f2', color: type === 'CE' ? '#16a34a' : '#dc2626', border: `1px solid ${type === 'CE' ? '#bbf7d0' : '#fecaca'}` }}>
        {type}
      </span>
    );
  };

  const SqBtn = ({ payload, keyId }) => {
    if (placed[keyId]) return <span style={s.placedBadge}>✓ Placed</span>;
    return (
      <button style={{ ...s.sqBtn, background: '#dc2626', color: '#fff', cursor: 'pointer' }}
        disabled={placing[keyId]} onClick={() => openConfirm(payload, keyId)}>
        {placing[keyId] ? 'Placing...' : 'Sq-Off'}
      </button>
    );
  };

  const NetQty = ({ qty }) => {
    const n = Number(qty);
    if (n > 0) return <span style={{ color: '#15803d', fontWeight: 600 }}>+{fmtQty(n)}</span>;
    if (n < 0) return <span style={{ color: '#dc2626', fontWeight: 600 }}>{fmtQty(n)}</span>;
    return <span style={{ color: '#9ca3af', fontWeight: 600 }}>0</span>;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4', border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: toast.type === 'error' ? '#dc2626' : '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13 }}>
          {toast.msg}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}>
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Confirm Square-Off</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>This action cannot be undone</div>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ background: '#f9fafb', border: '1px solid #eef0f3', borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
                {[
                  ['Symbol',     confirmModal.payload.symbol],
                  ['Exchange',   `${confirmModal.payload.exchange} / ${confirmModal.payload.segment}`],
                  ['Side',       confirmModal.payload.side],
                  ['Quantity',   confirmModal.payload.quantity],
                  ['Order Type', 'MARKET'],
                  ['Dealer ID',  clientData.dealerId],
                ].map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 14px', borderBottom: i < 5 ? '1px solid #f3f4f6' : 'none' }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{label}</span>
                    <span style={{ fontSize: 12, fontWeight: label === 'Dealer ID' ? 700 : 600, color: label === 'Side' ? '#dc2626' : '#111' }}>{value}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 12, padding: '9px 12px' }}>
                Order recorded under Dealer ID {clientData.dealerId}
              </div>
            </div>
            <div style={{ padding: '14px 20px 18px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, height: 40, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#374151', fontSize: 13, cursor: 'pointer' }} onClick={() => setConfirmModal(null)}>Cancel</button>
              <button style={{ flex: 2, height: 40, background: '#dc2626', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} onClick={handleSquareOff}>Confirm Square-Off</button>
            </div>
          </div>
        </div>
      )}

      {/* Topbar */}
      <div style={s.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={s.logoBox}><span style={s.logoTxt}>N</span></div>
          <span style={{ color: '#0f172a', fontWeight: 700, fontSize: 14 }}>Navia Backup</span>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <span style={{ color: '#64748b', fontSize: 12 }}>Dealer View</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={s.dealerBadge}>DEALER: {clientData?.dealerId}</span>
          <span style={s.uccBadge}>UCC: {clientData?.ucc}</span>
          <span style={{ fontSize: 12, color: '#64748b' }}>{clientData?.clientName}</span>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 24px' }}>
        <div style={s.infoBanner}>
          ⚠ Viewing <strong>{clientData?.clientName}</strong> ({clientData?.ucc}) as Dealer <strong>{clientData?.dealerId}</strong>. All sq-off orders recorded under your Dealer ID.
        </div>

        <div style={s.tabBar}>
          {tabs.map(t => (
            <button key={t.key} style={{ ...s.tab, ...(activeTab === t.key ? s.activeTab : {}) }} onClick={() => setActiveTab(t.key)}>
              {t.label}
              <span style={{ ...s.tabBadge, ...(activeTab === t.key ? s.activeTabBadge : {}) }}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* ── NET POSITION ── */}
        {activeTab === 'net' && (
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>Net Positions</div>
              <div style={s.cardSub}>B/F + Day combined · Sq-off enabled for open positions</div>
            </div>

          
            {/* F&O merged */}
            {netFOPositions.length > 0 && (
              <>
                <div style={{ ...s.sectionLabel, marginTop: dayEqOpen.length > 0 ? 8 : 0 }}>
                  F&O Net · {netFOPositions.length}
                  <span style={{ color: '#64748b', fontSize: 9, marginLeft: 6 }}>B/F + Day Combined</span>
                </div>
                <div style={{ ...s.tableHead, gridTemplateColumns: '2fr 1.2fr 70px 80px 80px 90px 100px 100px' }}>
                  {['Symbol & Strike', 'Expiry', 'Type', 'B/F Qty', 'Day Qty', 'Net Qty', 'Exchange', 'Action'].map(h => <div key={h} style={s.th}>{h}</div>)}
                </div>
                {netFOPositions.map((pos, i) => {
                  const key = `net_${pos.bf_id || pos.symbol}_${pos.strike_price}_${pos.option_type}_${i}`;
                  const net = Number(pos.net_qty);
                  return (
                    <div key={key} style={{ ...s.tableRow, gridTemplateColumns: '2fr 1.2fr 70px 80px 80px 90px 100px 100px', borderBottom: i < netFOPositions.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#111' }}>{pos.symbol}</div>
                        {pos.strike_price && <div style={{ fontSize: 11, color: '#6b7280' }}>Strike ₹{fmtStrike(pos.strike_price)}</div>}
                      </div>
                      <div style={s.cell}>{fmt(pos.expiry_date)}</div>
                      <div style={{ display: 'flex', alignItems: 'center' }}><TypeChip type={pos.option_type} /></div>
                      <div style={{ ...s.cell, color: '#d97706', fontWeight: 600 }}>{fmtQty(pos.bf_qty)}</div>
                      <div style={{ ...s.cell, color: pos.day_qty >= 0 ? '#15803d' : '#dc2626', fontWeight: 600 }}>{pos.day_qty > 0 ? '+' : ''}{fmtQty(pos.day_qty)}</div>
                      <NetQty qty={net} />
                      <div style={{ display: 'flex', alignItems: 'center' }}><ExBadge exchange={pos.exchange} segment={pos.segment} /></div>
                      {net === 0
                        ? <span style={s.placedBadge}>Squared Off</span>
                        : <SqBtn payload={{ symbol: pos.symbol, exchange: pos.exchange, segment: pos.segment || 'FO', side: net > 0 ? 'SELL' : 'BUY', quantity: Math.abs(net) }} keyId={key} />
                      }
                    </div>
                  );
                })}
              </>
            )}

            {netCount === 0 && (
              <div style={s.empty}>No open positions found.</div>
            )}
          </div>
        )}

        {/* ── DAY POSITION ── */}
        {activeTab === 'day' && (
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>Day Positions</div>
              <div style={s.cardSub}>Equity sq-off enabled · F&O view only</div>
            </div>
            {dayPos.length === 0 ? <div style={s.empty}>No day positions found for today.</div> : (
              <>
                {dayEquity.length > 0 && (
                  <>
                    <div style={s.sectionLabel}>Equity · Cash Market · {dayEquity.length}</div>
                    <div style={{ ...s.tableHead, gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 100px' }}>
                      {['Symbol', 'Buy Qty', 'Sell Qty', 'Net Qty', 'Exchange', 'Action'].map(h => <div key={h} style={s.th}>{h}</div>)}
                    </div>
                    {dayEquity.map((pos, i) => {
                      const key = `dayeq_${pos.symbol}_${pos.exchange}`;
                      const net = Number(pos.net_qty);
                      return (
                        <div key={key} style={{ ...s.tableRow, gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 100px', borderBottom: i < dayEquity.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                          <div style={{ fontWeight: 600, color: '#111' }}>{pos.symbol}</div>
                          <div style={{ ...s.cell, color: '#15803d', fontWeight: 600 }}>{fmtQty(pos.buy_qty)}</div>
                          <div style={{ ...s.cell, color: '#dc2626', fontWeight: 600 }}>{fmtQty(pos.sell_qty)}</div>
                          <NetQty qty={net} />
                          <div style={{ display: 'flex', alignItems: 'center' }}><ExBadge exchange={pos.exchange} segment={pos.segment} /></div>
                          {net === 0
                            ? <span style={s.disabledBadge}>Squared</span>
                            : placed[key]
                              ? <span style={s.placedBadge}>✓ Placed</span>
                              : <button style={{ ...s.sqBtn, background: '#dc2626', color: '#fff', cursor: 'pointer' }} onClick={() => openConfirm({ symbol: pos.symbol, exchange: pos.exchange, segment: pos.segment, side: net > 0 ? 'SELL' : 'BUY', quantity: Math.abs(net) }, key)}>Sq-Off</button>
                          }
                        </div>
                      );
                    })}
                  </>
                )}
                {dayOptions.length > 0 && (
                  <>
                    <div style={{ ...s.sectionLabel, marginTop: 8 }}>Options · F&O · {dayOptions.length} <span style={{ color: '#f59e0b', marginLeft: 8, fontSize: 10 }}>View only</span></div>
                    <div style={{ ...s.tableHead, gridTemplateColumns: '2fr 1.2fr 70px 1fr 1fr 1fr 100px' }}>
                      {['Symbol & Strike', 'Expiry', 'Type', 'Buy Qty', 'Sell Qty', 'Net Qty', 'Exchange'].map(h => <div key={h} style={s.th}>{h}</div>)}
                    </div>
                    {dayOptions.map((pos, i) => (
                      <div key={`do_${pos.id}`} style={{ ...s.tableRow, gridTemplateColumns: '2fr 1.2fr 70px 1fr 1fr 1fr 100px', borderBottom: i < dayOptions.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#111' }}>{pos.symbol}</div>
                          {pos.strike_price && <div style={{ fontSize: 11, color: '#6b7280' }}>Strike ₹{fmtStrike(pos.strike_price)}</div>}
                        </div>
                        <div style={s.cell}>{fmt(pos.expiry_date)}</div>
                        <div style={{ display: 'flex', alignItems: 'center' }}><TypeChip type={pos.option_type} /></div>
                        <div style={{ ...s.cell, color: '#15803d', fontWeight: 600 }}>{fmtQty(pos.buy_qty)}</div>
                        <div style={{ ...s.cell, color: '#dc2626', fontWeight: 600 }}>{fmtQty(pos.sell_qty)}</div>
                        <NetQty qty={pos.net_qty} />
                        <div style={{ display: 'flex', alignItems: 'center' }}><ExBadge exchange={pos.exchange} segment={pos.segment} /></div>
                      </div>
                    ))}
                  </>
                )}
                {dayFutures.length > 0 && (
                  <>
                    <div style={{ ...s.sectionLabel, marginTop: 8 }}>Futures · F&O · {dayFutures.length} <span style={{ color: '#f59e0b', marginLeft: 8, fontSize: 10 }}>View only</span></div>
                    <div style={{ ...s.tableHead, gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr' }}>
                      {['Symbol', 'Expiry', 'Buy Qty', 'Sell Qty', 'Net Qty'].map(h => <div key={h} style={s.th}>{h}</div>)}
                    </div>
                    {dayFutures.map((pos, i) => (
                      <div key={`df_${pos.id}`} style={{ ...s.tableRow, gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr', borderBottom: i < dayFutures.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                        <div style={{ fontWeight: 600, color: '#111' }}>{pos.symbol}</div>
                        <div style={s.cell}>{fmt(pos.expiry_date)}</div>
                        <div style={{ ...s.cell, color: '#15803d', fontWeight: 600 }}>{fmtQty(pos.buy_qty)}</div>
                        <div style={{ ...s.cell, color: '#dc2626', fontWeight: 600 }}>{fmtQty(pos.sell_qty)}</div>
                        <NetQty qty={pos.net_qty} />
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ── B/F POSITION — View only ── */}
        {activeTab === 'bf' && (
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>B/F Positions</div>
              <div style={s.cardSub}>Carried forward · View only</div>
            </div>
            {!bfData || (bfOptions.length === 0 && bfFutures.length === 0)
              ? <div style={s.empty}>No B/F positions found.</div>
              : (
                <>
                  {bfOptions.length > 0 && (
                    <>
                      <div style={s.sectionLabel}>Options · {bfOptions.length}</div>
                      <div style={{ ...s.tableHead, gridTemplateColumns: '2fr 1.2fr 70px 90px 120px' }}>
                        {['Symbol & Strike', 'Expiry', 'Type', 'Net Qty', 'Exchange'].map(h => <div key={h} style={s.th}>{h}</div>)}
                      </div>
                      {bfOptions.map((pos, i) => (
                        <div key={pos.bf_id} style={{ ...s.tableRow, gridTemplateColumns: '2fr 1.2fr 70px 90px 120px', borderBottom: i < bfOptions.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                          <div>
                            <div style={{ fontWeight: 600, color: '#111' }}>{pos.symbol}</div>
                            {pos.strike_price && <div style={{ fontSize: 11, color: '#6b7280' }}>Strike ₹{fmtStrike(pos.strike_price)}</div>}
                          </div>
                          <div style={s.cell}>{fmt(pos.expiry_date)}</div>
                          <div style={{ display: 'flex', alignItems: 'center' }}><TypeChip type={pos.option_type} /></div>
                          <NetQty qty={pos.qty_in_lots ?? pos.total_open_qty} />
                          <div style={{ display: 'flex', alignItems: 'center' }}><ExBadge exchange={pos.exchange} segment={pos.segment || 'FO'} /></div>
                        </div>
                      ))}
                    </>
                  )}
                  {bfFutures.length > 0 && (
                    <>
                      <div style={{ ...s.sectionLabel, marginTop: 16 }}>Futures · {bfFutures.length}</div>
                      <div style={{ ...s.tableHead, gridTemplateColumns: '2fr 1.2fr 90px 120px' }}>
                        {['Symbol', 'Expiry', 'Net Qty', 'Exchange'].map(h => <div key={h} style={s.th}>{h}</div>)}
                      </div>
                      {bfFutures.map((pos, i) => (
                        <div key={pos.bf_id} style={{ ...s.tableRow, gridTemplateColumns: '2fr 1.2fr 90px 120px', borderBottom: i < bfFutures.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                          <div style={{ fontWeight: 600, color: '#111' }}>{pos.symbol}</div>
                          <div style={s.cell}>{fmt(pos.expiry_date)}</div>
                          <NetQty qty={pos.qty_in_lots ?? pos.total_open_qty} />
                          <div style={{ display: 'flex', alignItems: 'center' }}><ExBadge exchange={pos.exchange} segment={pos.segment || 'FO'} /></div>
                        </div>
                      ))}
                    </>
                  )}
                  <div style={{ padding: '10px 20px', background: '#fffbeb', borderTop: '0.5px solid #fde68a', fontSize: 11, color: '#92400e' }}>
                    ⚠ View only · Square-off not available for B/F positions
                  </div>
                </>
              )}
          </div>
        )}

        {/* ── HOLDINGS ── */}
        {activeTab === 'holdings' && (
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>Holdings</div>
              <div style={s.cardSub}>NSDL DP holdings · Sq-off enabled</div>
            </div>
            {!holdingsData?.holdings?.length
              ? <div style={s.empty}>{holdingsData?.message || 'No holdings found.'}</div>
              : (
                <>
                  <div style={{ ...s.tableHead, gridTemplateColumns: '3fr 1fr 1fr 1fr 100px' }}>
                    {['Company Name', 'Qty', 'Close Price', 'Value', 'Action'].map(h => <div key={h} style={s.th}>{h}</div>)}
                  </div>
                  {holdingsData.holdings.map((h, i) => (
                    <div key={h.id} style={{ ...s.tableRow, gridTemplateColumns: '3fr 1fr 1fr 1fr 100px', borderBottom: i < holdingsData.holdings.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                      <div style={{ fontWeight: 600, color: '#111', fontSize: 13 }}>{h.company_name}</div>
                      <div style={{ ...s.cell, fontWeight: 600 }}>{h.quantity}</div>
                      <div style={s.cell}>₹{Number(h.close_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                      <div style={{ ...s.cell, fontWeight: 600, color: '#15803d' }}>₹{Number(h.total_value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                      <SqBtn payload={{ symbol: `${h.company_name} ; ${h.isin}`, exchange: 'NSE', segment: 'CM', side: 'SELL', quantity: h.quantity }} keyId={`hol_${h.isin}`} />
                    </div>
                  ))}
                  <div style={{ padding: '10px 20px', background: '#f0fdf4', borderTop: '0.5px solid #bbf7d0', fontSize: 11, color: '#166534' }}>
                    NSDL · as of {holdingsData.date} · Total: ₹{Number(holdingsData.total_value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </div>
                </>
              )}
          </div>
        )}

        {/* ── ORDERS ── */}
        {activeTab === 'orders' && (
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>Orders</div>
              <div style={s.cardSub}>{orders.length} orders placed in this session</div>
            </div>
            {orders.length === 0 ? <div style={s.empty}>No orders placed yet.</div> : (
              <>
                <div style={{ ...s.tableHead, gridTemplateColumns: '2fr 90px 60px 60px 60px 80px 100px 100px' }}>
                  {['Symbol', 'Exchange', 'Side', 'Qty', 'Type', 'Status', 'Dealer', 'Time'].map(h => <div key={h} style={s.th}>{h}</div>)}
                </div>
                {orders.map((ord, i) => (
                  <div key={ord.order_id} style={{ ...s.tableRow, gridTemplateColumns: '2fr 90px 60px 60px 60px 80px 100px 100px', borderBottom: i < orders.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                    <div style={{ fontWeight: 600, color: '#111' }}>{ord.symbol}</div>
                    <div style={{ display: 'flex', alignItems: 'center' }}><ExBadge exchange={ord.exchange} segment={ord.segment || ''} /></div>
                    <div style={{ ...s.cell, fontWeight: 600, color: ord.side === 'SELL' ? '#dc2626' : '#15803d' }}>{ord.side}</div>
                    <div style={s.cell}>{ord.quantity}</div>
                    <div style={s.cell}>{ord.order_type}</div>
                    <span style={{ ...s.statusBadge, background: ord.status === 'TRADED' ? '#f0fdf4' : ord.status === 'RECEIVED' ? '#fffbeb' : '#fef2f2', color: ord.status === 'TRADED' ? '#166534' : ord.status === 'RECEIVED' ? '#92400e' : '#dc2626', border: `0.5px solid ${ord.status === 'TRADED' ? '#bbf7d0' : ord.status === 'RECEIVED' ? '#fcd34d' : '#fecaca'}` }}>{ord.status}</span>
                    <div style={{ ...s.cell, fontWeight: 600, color: '#c2410c' }}>{ord.dealer_id || clientData.dealerId}</div>
                    <div style={{ ...s.cell, color: '#64748b' }}>{new Date(ord.placed_at).toLocaleTimeString('en-IN')}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  center:        { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: "'Segoe UI', Arial, sans-serif" },
  topbar:        { background: '#fff', borderBottom: '0.5px solid #e2e8f0', padding: '0 28px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: "'Segoe UI', Arial, sans-serif" },
  logoBox:       { width: 28, height: 28, background: '#1d4ed8', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoTxt:       { color: '#fff', fontWeight: 800, fontSize: 12 },
  dealerBadge:   { background: '#fff7ed', color: '#c2410c', border: '0.5px solid #fed7aa', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  uccBadge:      { background: '#eff6ff', color: '#1d4ed8', border: '0.5px solid #bfdbfe', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  infoBanner:    { background: '#fffbeb', border: '0.5px solid #fcd34d', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 12, color: '#92400e' },
  tabBar:        { display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 0, overflowX: 'auto' },
  tab:           { display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '10px 14px', marginBottom: '-1px', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  activeTab:     { color: '#1d4ed8', borderBottom: '2px solid #1d4ed8' },
  tabBadge:      { background: '#f3f4f6', borderRadius: 20, color: '#6b7280', fontSize: 10, fontWeight: 600, padding: '1px 6px' },
  activeTabBadge:{ background: '#eff6ff', color: '#1d4ed8' },
  card:          { background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: '0 0 10px 10px', overflow: 'hidden' },
  cardHead:      { padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0' },
  cardTitle:     { fontSize: 13, fontWeight: 600, color: '#0f172a' },
  cardSub:       { fontSize: 11, color: '#64748b', marginTop: 2 },
  tableHead:     { display: 'grid', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' },
  tableRow:      { display: 'grid', padding: '12px 20px', alignItems: 'center' },
  th:            { fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 },
  cell:          { fontSize: 12, color: '#374151' },
  exBadge:       { fontSize: 9, fontWeight: 600, padding: '2px 5px', borderRadius: 4, display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3, whiteSpace: 'nowrap', maxWidth: 65 },
  chip:          { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 34 },
  futBadge:      { fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: '#f3f4f6', color: '#374151', border: '0.5px solid #e5e7eb', display: 'inline-flex', alignItems: 'center' },
  sqBtn:         { padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600 },
  placedBadge:   { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166634', fontSize: 11, fontWeight: 500, padding: '3px 8px', whiteSpace: 'nowrap' },
  disabledBadge: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, color: '#9ca3af', fontSize: 11, padding: '3px 8px' },
  empty:         { padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 },
  sectionLabel:  { padding: '10px 20px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f9fafb', borderBottom: '0.5px solid #e2e8f0', borderTop: '0.5px solid #e2e8f0' },
  statusBadge:   { fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', display: 'inline-block' },
};